// ─────────────────────────────────────────────────────────────────────────────
// Ludo board geometry — the single source of truth used by the engine, the
// socket layer and the renderer. Cells are described on a 15×15 grid.
// x = column, y = row. The renderer scales each unit by a pixel size.
// ─────────────────────────────────────────────────────────────────────────────

export type Color = 'red' | 'green' | 'yellow' | 'blue';
export const COLORS: Color[] = ['red', 'green', 'yellow', 'blue'];
export const COLOR_LABELS: Record<Color, string> = {
  red: 'Red',
  green: 'Green',
  yellow: 'Yellow',
  blue: 'Blue',
};

// The 52 common-track cells, in movement order (counter-clockwise). Cell 0 is
// Red's start square; each quarter (13 cells) belongs to one player.
export const RING: { x: number; y: number }[] = [
  { x: 0, y: 6 }, { x: 1, y: 6 }, { x: 2, y: 6 }, { x: 3, y: 6 }, { x: 4, y: 6 }, { x: 5, y: 6 },
  { x: 6, y: 5 }, { x: 6, y: 4 }, { x: 6, y: 3 }, { x: 6, y: 2 }, { x: 6, y: 1 }, { x: 6, y: 0 },
  { x: 7, y: 0 }, { x: 8, y: 0 }, { x: 8, y: 1 }, { x: 8, y: 2 }, { x: 8, y: 3 }, { x: 8, y: 4 }, { x: 8, y: 5 },
  { x: 9, y: 6 }, { x: 10, y: 6 }, { x: 11, y: 6 }, { x: 12, y: 6 }, { x: 13, y: 6 }, { x: 14, y: 6 },
  { x: 14, y: 7 }, { x: 14, y: 8 }, { x: 13, y: 8 }, { x: 12, y: 8 }, { x: 11, y: 8 }, { x: 10, y: 8 }, { x: 9, y: 8 },
  { x: 8, y: 9 }, { x: 8, y: 10 }, { x: 8, y: 11 }, { x: 8, y: 12 }, { x: 8, y: 13 }, { x: 8, y: 14 },
  { x: 7, y: 14 }, { x: 6, y: 14 }, { x: 6, y: 13 }, { x: 6, y: 12 }, { x: 6, y: 11 }, { x: 6, y: 10 }, { x: 6, y: 9 },
  { x: 5, y: 8 }, { x: 4, y: 8 }, { x: 3, y: 8 }, { x: 2, y: 8 }, { x: 1, y: 8 }, { x: 0, y: 8 },
  { x: 0, y: 7 },
];

/** Ring index where each color's tokens first land (the colored start cell),
 *  directly adjacent to the start of that color's home lane. */
export const START_INDEX: Record<Color, number> = {
  red: 1,
  green: 14,
  yellow: 27,
  blue: 40,
};

export interface Cell {
  x: number;
  y: number;
}

/** Home lanes, ordered from the ring exit to the centre.
 *  Lane step `i` (0..5) is the cell for a token at distance 52+i. */
export const HOME_LANE: Record<Color, Cell[]> = {
  red: [
    { x: 1, y: 7 }, { x: 2, y: 7 }, { x: 3, y: 7 }, { x: 4, y: 7 }, { x: 5, y: 7 }, { x: 6, y: 7 },
  ],
  green: [
    { x: 7, y: 1 }, { x: 7, y: 2 }, { x: 7, y: 3 }, { x: 7, y: 4 }, { x: 7, y: 5 }, { x: 7, y: 6 },
  ],
  yellow: [
    { x: 13, y: 7 }, { x: 12, y: 7 }, { x: 11, y: 7 }, { x: 10, y: 7 }, { x: 9, y: 7 }, { x: 8, y: 7 },
  ],
  blue: [
    { x: 7, y: 13 }, { x: 7, y: 12 }, { x: 7, y: 11 }, { x: 7, y: 10 }, { x: 7, y: 9 }, { x: 7, y: 8 },
  ],
};

/** The centre square every home lane ends at. */
export const HOME_CENTER: Cell = { x: 7, y: 7 };

/** 6×6 corner yards (base areas) for token spawning, keyed by color. */
export const YARD_ORIGIN: Record<Color, Cell> = {
  red: { x: 0, y: 0 },
  green: { x: 9, y: 0 },
  yellow: { x: 9, y: 9 },
  blue: { x: 0, y: 9 },
};

/** Four spawn slots (cell coords) inside each yard, symmetric about the yard's
 *  centre (local 3×CELL) so tokens sit visually centred in the box. */
export const YARD_SLOTS: Record<Color, Cell[]> = {
  red: [
    { x: 1, y: 1 }, { x: 1, y: 4 }, { x: 4, y: 1 }, { x: 4, y: 4 },
  ],
  green: [
    { x: 10, y: 1 }, { x: 10, y: 4 }, { x: 13, y: 1 }, { x: 13, y: 4 },
  ],
  yellow: [
    { x: 10, y: 10 }, { x: 10, y: 13 }, { x: 13, y: 10 }, { x: 13, y: 13 },
  ],
  blue: [
    { x: 1, y: 10 }, { x: 1, y: 13 }, { x: 4, y: 10 }, { x: 4, y: 13 },
  ],
};

/** Safe squares: the four start cells (1/14/27/40) plus the four star cells.
 *  Each star sits 8 steps ahead of its colour's start (distance 9), so a token
 *  launched to its start reaches the star on its 8th moving roll. */
const SAFE_RING_INDICES = new Set([1, 14, 27, 40, 9, 22, 35, 48]);

export function isSafeRingIndex(ringIndex: number): boolean {
  return SAFE_RING_INDICES.has(ringIndex);
}

/** Key used to compare "visual position" of tokens on the board. */
export function cellKey(c: Cell): string {
  return `${c.x},${c.y}`;
}

/** Distance model: 0 = yard, 1..51 = common track, 52..56 = home lane,
 *  57 = finished (in the centre). Map a distance to its on-board cell. */
export function cellOf(color: Color, d: number): Cell {
  if (d >= 1 && d <= 51) {
    return RING[(START_INDEX[color] + d - 1) % RING.length];
  }
  if (d >= 52 && d <= 56) {
    return HOME_LANE[color][d - 52];
  }
  return HOME_CENTER;
}